import { Form, FormItem, useFormContext } from '@magicdoor/form';
import { useLocalization } from '@magicdoor/ui';
import { createEffect, createMemo, onMount, Show } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import EmailSent from '~/assets/auth/emailSent.svg?component-solid';
import { Input } from '~/components/auth/InputField';
import { ReturnButton } from '~/components/auth/ReturnButton';
import { restoreEmail } from '~/components/auth/emailStorage';
import { PolicyCheckbox } from '~/components/auth/sign-in/PolicyCheckbox';
import { SignInTitle } from '~/components/auth/sign-in/SignInTitle';
import { Button } from '~/components/common/Buttons';
import { LabeledSelect } from '~/components/common/Inputs/LabeledSelect';
import { MagicLinkLoginProvider, MagicLinkStep, useMagicLinkLogin } from '~/contexts/local';
import type { Component } from 'solid-js';
import type { SendMagicLinkDto } from '~/swagger/Api';

const MagicLinkSignIn = () => {
  const { t } = useLocalization();
  const { loginStep, sendMagicLink, tip, setLoginStep } = useMagicLinkLogin();

  const stepMap: Record<MagicLinkStep, Component> = {
    [MagicLinkStep.InputEmail]: MagicLinkInputEmail,
    [MagicLinkStep.SelectCompany]: MagicLinkSelectCompany,
  };

  const handleSubmit = async (store: any) => {
    await sendMagicLink(store as SendMagicLinkDto);
  };

  return (
    <Form onFormSubmit={handleSubmit}>
      <SignInTitle title={t('Sign in')} subTitle="With magic link" />
      <div class="flex flex-col gap-4">
        <Dynamic component={stepMap[loginStep()]} />

        <Show when={tip()}>
          <div class="flex items-center gap-2 text-sm font-normal text-essential-colour">
            <EmailSent class="shrink-0" />
            <span>{t(tip() as string)}</span>
          </div>
        </Show>

        <PolicyCheckbox />

        <Button loading={sendMagicLink.loading} rounded="full" class="flex w-full justify-center" type="submit">
          {t('Send magic link')}
        </Button>

        <Show when={loginStep() === MagicLinkStep.SelectCompany}>
          <ReturnButton
            toPreviousStep={() => {
              setLoginStep(MagicLinkStep.InputEmail);
            }}
          />
        </Show>
      </div>
    </Form>
  );
};

const MagicLinkInputEmail = () => {
  const { t } = useLocalization();
  const form = useFormContext();

  onMount(() => {
    restoreEmail(form);
  });

  return (
    <Input
      label={t('Email')}
      placeholder="mail@magicdoor.com"
      formFieldName="email"
      rules={[
        { required: true, message: t('{name} is required', { name: t('Email') }) },
        { type: 'email', message: t('{name} is invalid', { name: t('Email') }) },
      ]}
    />
  );
};

const MagicLinkSelectCompany = () => {
  const form = useFormContext();
  const { companies } = useMagicLinkLogin();
  const options = createMemo(() => companies().map((company) => ({ label: company.name, value: company.id })));
  createEffect(() => {
    if (companies().length) {
      form.setFieldValue('companyId', companies()[0].id);
    }
  });
  return (
    <FormItem removeValueOnClean formFieldName="companyId" component={LabeledSelect} selectClass="h-12 rounded-full" options={options()} />
  );
};

export const MagicLinkSignInView = () => {
  return (
    <MagicLinkLoginProvider>
      <MagicLinkSignIn />
    </MagicLinkLoginProvider>
  );
};
